import React, { Component } from 'react';
import { array, bool, func, object, shape, string, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { getUserQueryParamNames } from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { getUsersById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { NamedRedirect, Page, SecondaryButton } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import TopbarNavigation from '../TopbarContainer/Topbar/TopbarNavigation/TopbarNavigation';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupUserFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  createSearchResultSchema,
  omitLimitedUserFieldParams,
} from './SearchUsersPage.shared';

import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import FilterComponent from './FilterComponent';
import AlphabetFilter from './AlphabetFilter/AlphabetFilter';
import SearchFiltersMobile from '../SearchPage/SearchFiltersMobile/SearchFiltersMobile';
import NoSearchResultsMaybe from '../SearchPage/NoSearchResultsMaybe/NoSearchResultsMaybe';

import css from './SearchUsersPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

export class SearchUsersPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config } = this.props;
    const { userFields: userFieldsConfig } = config.user || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getUserQueryParamNames(userFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params
    const queryParams = omit(urlQueryParams, filterQueryParamNames);
    history.push(
      createResourceLocatorString('SearchUsersPage', routeConfiguration, {}, queryParams)
    );
  }

  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config } = this.props;
    const { userFields: userFieldsConfig } = config?.user || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };
        const newQueryParams = omitLimitedUserFieldParams(
          {
            ...mergedQueryParams,
            ...updatedURLParams,
          },
          userFieldsConfig,
          defaultFiltersConfig,
          listingCategories
        );

        return {
          currentQueryParams: newQueryParams,
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;

          history.push(
            createResourceLocatorString('SearchUsersPage', routeConfiguration, {}, searchParams)
          );
        }
      };

      this.setState(updater, callback);
    };
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      users,
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchUsersError,
      searchParams,
      routeConfiguration,
      config,
    } = this.props;

    const { userFields: userFieldsConfig } = config?.user || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      userFieldsConfig,
      defaultFiltersConfig,
      listingCategories
    );
    const validQueryParams = urlQueryParams;

    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel'].includes(f.key)
    );
    const [customPrimaryFilters, customSecondaryFilters] = groupUserFieldConfigs(userFieldsConfig);
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...customSecondaryFilters,
    ];

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? users.length
        : 0;
    const usersAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const noResultsInfo = (
      <NoSearchResultsMaybe
        resultsAreLoaded={usersAreLoaded}
        totalItems={totalItems}
        noResultsInfo={intl.formatMessage({
          id: 'SearchUsersPage.noResults',
        })}
        searchUsersError={searchUsersError}
        isValidDatesFilter={isValidDatesFilter}
        location={location}
        resetAll={this.resetAll}
      />
    );

    const { title, description, schema } = createSearchResultSchema(
      users,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        <TopbarNavigation />
        <div className={css.layoutWrapperContainer}>
          <MainPanelHeader className={css.mainPanel} users={users} totalItems={totalItems} />
          <div className={css.layoutWrapperContent}>
            <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
              <div className={css.filterColumnContent}>
                {availableFilters.map(filterConfig => {
                  const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
                    filterConfig.key
                  }`;
                  return (
                    <FilterComponent
                      key={key}
                      idPrefix="SearchFiltersDesktop"
                      className={css.filter}
                      config={filterConfig}
                      currentPage="SearchUsersPage"
                      listingCategories={listingCategories}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                      isDesktop
                    />
                  );
                })}
                <SecondaryButton
                  className={css.resetAllButton}
                  onClick={e => this.handleResetAll(e)}
                >
                  <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
                </SecondaryButton>
              </div>
            </aside>

            <div className={css.layoutWrapperMain} role="main">
              <div className={css.searchResultContainer}>
                <SearchFiltersMobile
                  className={css.searchFiltersMobileList}
                  urlQueryParams={validQueryParams}
                  resultsAreLoaded={usersAreLoaded}
                  resultsCount={totalItems}
                  searchInProgress={searchInProgress}
                  searchUsersError={searchUsersError}
                  showAsModalMaxWidth={MODAL_BREAKPOINT}
                  onManageDisableScrolling={onManageDisableScrolling}
                  onOpenModal={this.onOpenMobileModal}
                  onCloseModal={this.onCloseMobileModal}
                  resetAll={this.resetAll}
                  selectedFiltersCount={selectedFiltersCountForMobile}
                  showResultsMessage={intl.formatMessage(
                    { id: 'SearchUsersPage.showResults' },
                    { count: totalItems }
                  )}
                  isMapVariant={false}
                  cancelPageName="SearchUsersPage"
                >
                  {availableFilters.map(filterConfig => {
                    const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                      filterConfig.key
                    }`;

                    return (
                      <FilterComponent
                        key={key}
                        idPrefix="SearchFiltersMobile"
                        config={filterConfig}
                        currentPage="SearchUsersPage"
                        listingCategories={listingCategories}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        liveEdit
                        showAsPopup={false}
                      />
                    );
                  })}
                </SearchFiltersMobile>
                <div
                  className={classNames(css.searchResultsPanel, {
                    [css.newSearchInProgress]: !(usersAreLoaded || searchUsersError),
                  })}
                >
                  <AlphabetFilter
                    urlQueryParams={validQueryParams}
                    getHandleChangedValueFn={this.getHandleChangedValueFn}
                  />
                  {noResultsInfo}
                  <SearchResultsPanel
                    className={css.searchUsersPanel}
                    users={users}
                    pagination={usersAreLoaded ? pagination : null}
                    search={parse(location.search)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

SearchUsersPageComponent.defaultProps = {
  users: [],
  pagination: null,
  searchUsersError: null,
  searchParams: {},
};

SearchUsersPageComponent.propTypes = {
  users: array,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchUsersError: propTypes.error,
  searchParams: object,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchUsersPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchUsersError = props.searchUsersError;
  if (isForbiddenError(searchUsersError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchUsersError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchUsersError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchUsersPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchUsersError,
    searchParams,
  } = state.SearchUsersPage;
  const users = getUsersById(state, currentPageResultIds);

  return {
    currentUser,
    users,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchUsersError,
    searchParams,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchUsersPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedSearchUsersPage);

export default SearchUsersPage;
